/**
 * Interview API hooks(React Query)。
 * 同時服務客戶與廠商兩種訪談 owner —— 透過 ownerType 參數切換 API 路徑與 query key。
 *   - ownerType='customer' → /customers/:id/interviews + /interviews/:id
 *   - ownerType='supplier' → /suppliers/:id/interviews + /supplier-interviews/:id
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type {
  ApiList,
  ApiSuccess,
  CreateInterviewInput,
  CreateInterviewReceivableInput,
  CreateInterviewReceivableResponse,
  InterviewResponse,
  InterviewTypeResponse,
  UpdateInterviewInput,
} from '@app/shared';
import { apiClient } from '../../lib/api-client';

export type InterviewOwnerType = 'customer' | 'supplier';

/** 集合層級 URL 前綴('customers' 或 'suppliers'),用於 list/create。 */
function collectionPath(t: InterviewOwnerType): string {
  return t === 'customer' ? 'customers' : 'suppliers';
}

/** 單筆 URL 前綴('interviews' 或 'supplier-interviews'),用於 update/delete/get one。 */
function singlePath(t: InterviewOwnerType): string {
  return t === 'customer' ? 'interviews' : 'supplier-interviews';
}

/**
 * REQ-20260421009 §2 延伸:取某 owner 訪談的「總筆數」(不受類別篩選影響)。
 * 用 pageSize=1 最小 payload;只讀 meta.total。
 */
export function useInterviewsTotalAll(
  ownerId: number | undefined,
  ownerType: InterviewOwnerType,
) {
  return useQuery({
    queryKey: ['interviews-total-all', ownerType, ownerId],
    enabled: ownerId != null,
    staleTime: 1000 * 30,
    queryFn: async (): Promise<number> => {
      const res = await apiClient.get<ApiList<InterviewResponse>>(
        `/${collectionPath(ownerType)}/${ownerId}/interviews`,
        { params: { page: 1, pageSize: 1 } },
      );
      return res.data.meta.total;
    },
  });
}

// ── types reference(下拉用,簡單 cache)─────────────────────────
// 訪談類別 reference 兩邊共用,不用分 owner。
export function useInterviewTypes() {
  return useQuery({
    queryKey: ['interview-types'],
    staleTime: 1000 * 60 * 60, // 1h
    queryFn: async (): Promise<InterviewTypeResponse[]> => {
      const res = await apiClient.get<ApiSuccess<InterviewTypeResponse[]>>('/interview-types');
      return res.data.data;
    },
  });
}

// ── list(某 owner 的訪談,無限捲動)─────────────────────────────
const INTERVIEWS_PAGE_SIZE = 25;

export interface UseInterviewsFilter {
  interviewTypeId?: number | null;
  /** REQ-20260803003:關鍵字搜尋(比對 notes / contact)。 */
  q?: string | undefined;
}

export function useInterviews(
  ownerId: number | undefined,
  ownerType: InterviewOwnerType,
  filter: UseInterviewsFilter = {},
) {
  const typeId = filter.interviewTypeId ?? null;
  const q = filter.q ?? '';
  return useInfiniteQuery({
    queryKey: ['interviews', ownerType, ownerId, { typeId, q }],
    enabled: ownerId != null,
    initialPageParam: 1,
    queryFn: async ({ pageParam }): Promise<ApiList<InterviewResponse>> => {
      const res = await apiClient.get<ApiList<InterviewResponse>>(
        `/${collectionPath(ownerType)}/${ownerId}/interviews`,
        {
          params: {
            page: pageParam,
            pageSize: INTERVIEWS_PAGE_SIZE,
            ...(typeId ? { interviewTypeId: typeId } : {}),
            ...(q ? { q } : {}),
          },
        },
      );
      return res.data;
    },
    getNextPageParam: (lastPage) => {
      const loaded = lastPage.meta.page * lastPage.meta.pageSize;
      return loaded < lastPage.meta.total ? lastPage.meta.page + 1 : undefined;
    },
  });
}

/**
 * 單筆訪談(`GET /interviews/:id`)。出貨管理的建單 / 編輯表單要靠它的 `attachments`
 * 列出稿樣候選圖片(REQ-20260811004)—— 訪談頁那條路徑的 interview 是父層直接傳進來的,
 * 只有「先有 interviewId、才要去把那筆訪談撈回來」的出貨表單需要這個 hook。
 * 只服務 customer 訪談,與 `/interviews/:id/receivable` 同一個範圍。
 */
export function useInterview(interviewId: number | null | undefined) {
  return useQuery({
    queryKey: ['interview', interviewId],
    enabled: interviewId != null,
    queryFn: async (): Promise<InterviewResponse> => {
      const res = await apiClient.get<ApiSuccess<InterviewResponse>>(
        `/interviews/${interviewId}`,
      );
      return res.data.data;
    },
  });
}

// ── create ─────────────────────────────────────────────────────
export function useCreateInterview(ownerId: number, ownerType: InterviewOwnerType) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateInterviewInput): Promise<InterviewResponse> => {
      const res = await apiClient.post<ApiSuccess<InterviewResponse>>(
        `/${collectionPath(ownerType)}/${ownerId}/interviews`,
        input,
      );
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['interviews', ownerType, ownerId],
        exact: false,
      });
      void qc.invalidateQueries({
        queryKey: ['interviews-total-all', ownerType, ownerId],
      });
    },
  });
}

// ── update / delete(以 interview id 為主)─────────────────────
export function useUpdateInterview(ownerId: number, ownerType: InterviewOwnerType) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      input,
    }: {
      id: number;
      input: UpdateInterviewInput;
    }): Promise<InterviewResponse> => {
      const res = await apiClient.patch<ApiSuccess<InterviewResponse>>(
        `/${singlePath(ownerType)}/${id}`,
        input,
      );
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['interviews', ownerType, ownerId],
        exact: false,
      });
      void qc.invalidateQueries({
        queryKey: ['interviews-total-all', ownerType, ownerId],
      });
    },
  });
}

/**
 * 切換訪談的「每日追蹤提醒」(鈴鐺按鈕)。
 * 直接複用 PATCH /interviews/:id 或 /supplier-interviews/:id 送 notifyDaily。
 */
export function useToggleInterviewNotify(ownerId: number, ownerType: InterviewOwnerType) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: number; notifyDaily: boolean }): Promise<InterviewResponse> => {
      const res = await apiClient.patch<ApiSuccess<InterviewResponse>>(
        `/${singlePath(ownerType)}/${args.id}`,
        { notifyDaily: args.notifyDaily },
      );
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['interviews', ownerType, ownerId],
        exact: false,
      });
      void qc.invalidateQueries({
        queryKey: ['interviews-total-all', ownerType, ownerId],
      });
    },
  });
}

/**
 * 從訪談一鍵建立應收單(REQ-20260730)。只服務 customer 訪談(interviews.controller.ts 只開放
 * `/interviews/:id/receivable`,supplier 訪談無應收概念)。
 * 成功後 invalidate 該客戶的 ar-settlements 查詢,維持資料新鮮度(即使該頁目前不會顯示這筆新單)。
 */
export function useCreateInterviewReceivable(customerId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      interviewId,
      input,
    }: {
      interviewId: number;
      input: CreateInterviewReceivableInput;
    }): Promise<CreateInterviewReceivableResponse> => {
      const res = await apiClient.post<ApiSuccess<CreateInterviewReceivableResponse>>(
        `/interviews/${interviewId}/receivable`,
        input,
      );
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ar-settlements', 'by-customer', customerId], exact: false });
      void qc.invalidateQueries({ queryKey: ['ar-settlements', 'by-customer-total', customerId] });
      // REQ-20260809003:訪談列表帶著 hasReceivable(新增應收按鈕的紅黑燈號),
      // 建單後要重抓才會由紅轉黑。queryKey 尾端還有篩選參數,所以 exact:false。
      void qc.invalidateQueries({ queryKey: ['interviews', 'customer', customerId], exact: false });
      // REQ-20260810002:建了私帳單之後,月結對帳單(少一張單)、個人未結明細、
      // 以及「用過的私帳對象姓名」三份資料都變了 —— 全部掛在 ['statements'] 底下。
      void qc.invalidateQueries({ queryKey: ['statements'] });
    },
  });
}

export function useDeleteInterview(ownerId: number, ownerType: InterviewOwnerType) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await apiClient.delete(`/${singlePath(ownerType)}/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['interviews', ownerType, ownerId],
        exact: false,
      });
      void qc.invalidateQueries({
        queryKey: ['interviews-total-all', ownerType, ownerId],
      });
    },
  });
}
